import '../css/App.css';

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shopFaq, groupItemsByCollection } from '../shopItems.js';
import { matchesQuery } from '../search';
import { useCatalog } from '../catalogContext';
import { useCart } from '../cartContext';
import { useAccount } from '../accountContext';
import StorefrontLayout from './StorefrontLayout';
import ProductCard from './ProductCard';

const Shop = () => {
    const { shopItems, shopCategories, refresh } = useCatalog();
    const { clearCart } = useCart();
    const { refresh: refreshAccount } = useAccount();
    const [category, setCategory] = useState('All');
    const [subscribed, setSubscribed] = useState(false);
    const [params, setParams] = useSearchParams();
    const [notice, setNotice] = useState('');
    const [order, setOrder] = useState(null);
    const handledCheckout = useRef(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const status = params.get('checkout');
        if (!status || handledCheckout.current) {
            return;
        }
        handledCheckout.current = true;
        const sessionId = params.get('session_id');
        if (status === 'success') {
            setNotice('success');
            const finish = () => {
                clearCart();
                refresh();
                refreshAccount();
            };
            if (sessionId) {
                fetch(`/api/checkout/confirm?session_id=${encodeURIComponent(sessionId)}`)
                    .then((response) => (response.ok ? response.json() : Promise.reject()))
                    .then((data) => {
                        if (data?.ok) {
                            setOrder(data);
                        }
                    })
                    .catch(() => {})
                    .finally(finish);
            } else {
                finish();
            }
        } else if (status === 'cancel') {
            setNotice('cancel');
        }
        setParams({}, { replace: true });
    }, [params, setParams, clearCart, refresh, refreshAccount]);

    const query = (params.get('q') || '').trim();
    const visibleItems = (category === 'All'
        ? shopItems
        : category === 'Sticker'
            ? shopItems.filter((item) => String(item.format || '').toLowerCase() === 'sticker')
            : shopItems.filter((item) => (
                item.category === category
                && String(item.format || '').toLowerCase() !== 'sticker'
            ))
    ).filter((item) => matchesQuery(item, query));
    const { groups, uncategorized } = groupItemsByCollection(visibleItems);

    return (
        <StorefrontLayout className="shopPage">
            <h1 className="shopPageTitle">Shop</h1>
            {notice ?
                <section className="shopHero">
                    {notice === 'success' ?
                    <aside className="shopOrderConfirm" aria-live="polite">
                        <p className="shopOrderConfirmKicker">Order confirmed</p>
                        <h2 className="shopOrderConfirmTitle">Thank you</h2>
                        <p className="shopOrderConfirmCopy">
                            {order?.email ?
                                `Payment is complete. A receipt is on its way to ${order.email}.`
                                :
                                'Payment is complete. Stripe is sending a receipt to the email used at checkout.'}
                        </p>
                        <dl className="shopOrderConfirmMeta">
                            <div>
                                <dt>Order number</dt>
                                <dd>{order?.receiptNumber || 'Confirming…'}</dd>
                            </div>
                            {order?.amount != null ?
                                <div>
                                    <dt>Total</dt>
                                    <dd>${Number(order.amount).toFixed(2)} USD</dd>
                                </div>
                                : null}
                        </dl>
                        {order?.receiptUrl ?
                            <a className="shopOrderConfirmLink" href={order.receiptUrl} target="_blank" rel="noreferrer">
                                View receipt
                            </a>
                            : null}
                    </aside>
                    : null}
                    {notice === 'cancel' ?
                    <aside className="shopOrderConfirm shopOrderConfirmCancel" aria-live="polite">
                        <p className="shopOrderConfirmKicker">Checkout</p>
                        <h2 className="shopOrderConfirmTitle">Order not placed</h2>
                        <p className="shopOrderConfirmCopy">
                            Payment was canceled. Your cart is saved if you’d like to try again.
                        </p>
                    </aside>
                    : null}
                </section>
                : null}

            <section className="shopSection">
                {query ?
                    <div className="shopSearchStatus">
                        <p>
                            {visibleItems.length ?
                                `Results for “${query}”`
                                :
                                `No pieces match “${query}”`}
                        </p>
                        <button
                            type="button"
                            className="shopSearchClear"
                            onClick={() => {
                                const next = new URLSearchParams(params);
                                next.delete('q');
                                setParams(next, { replace: true });
                            }}
                        >
                            Clear search
                        </button>
                    </div>
                    : null}
                <div className="shopFilters">
                    {shopCategories.map((name) => (
                        <button
                            key={name}
                            className={name === category ? 'shopFilter shopFilterActive' : 'shopFilter'}
                            onClick={() => setCategory(name)}
                        >
                            {name}
                        </button>
                    ))}
                </div>
                <div className="shopCatalog">
                    {groups.map((group) => (
                        <div className="shopCollectionBlock" key={group.id}>
                            <h2 className="shopCollectionHeading">
                                {group.jp ? <>
                                    <span className="shopCollectionJp">{group.jp}</span>
                                    <span aria-hidden="true"> — </span>
                                </> : null}
                                {group.name}
                            </h2>
                            <div className="productGrid">
                                {group.items.map((item) => (
                                    <ProductCard key={item.id} item={item} />
                                ))}
                            </div>
                        </div>
                    ))}
                    {uncategorized.length > 0 ?
                        <div className="shopCollectionBlock">
                            <div className="productGrid">
                                {uncategorized.map((item) => (
                                    <ProductCard key={item.id} item={item} />
                                ))}
                            </div>
                        </div>
                        : null}
                </div>
            </section>

            <section className="shopNewsletter">
                <h2 className="shopSectionTitle">Join the Print Club</h2>
                <p className="shopNewsletterText">
                    Get 10% off your first order + first access to new editions, studio news, and print drops.
                </p>
                {subscribed ?
                    <div className="shopNewsletterThanks">Thank you for subscribing!</div>
                    :
                    <form
                        className="shopNewsletterForm"
                        onSubmit={(event) => {
                            event.preventDefault();
                            setSubscribed(true);
                        }}
                    >
                        <input className="shopNewsletterInput" type="email" required placeholder="Email address" />
                        <button className="shopNewsletterButton" type="submit">Sign Up</button>
                    </form>
                }
            </section>

            <section className="shopSection shopFaqSection">
                <h2 className="shopSectionTitle">Frequently Asked Questions</h2>
                <div className="shopFaqList">
                    {shopFaq.map((faq) => (
                        <details className="shopFaqItem" key={faq.question}>
                            <summary className="shopFaqQuestion">{faq.question}</summary>
                            <p className="shopFaqAnswer">{faq.answer}</p>
                        </details>
                    ))}
                </div>
            </section>
        </StorefrontLayout>
    );
};

export default Shop;
